import {
  Badge,
  Container,
  EmptyState,
  Flex,
  Heading,
  Table,
  VStack,
  Link,
} from "@chakra-ui/react"
import { FiSearch } from 'react-icons/fi';
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { z } from "zod"

import { ProviderClients as ProviderClientsApi, ProviderClientTypes, ProviderGroups } from "@/client"
import AddProviderClient from "@/components/ProviderClients/AddProviderClient"
import PendingProviderClients from "@/components/ProviderClients/PendingProviderClients"
import {
  PaginationItems,
  PaginationNextTrigger,
  PaginationPrevTrigger,
  PaginationRoot,
} from "@/components/ui/pagination.tsx"

const ProviderClientsSearchSchema = z.object({
  page: z.number().catch(1),
})

const PER_PAGE = 5

function getProviderClientsQueryOptions({ page, providerId }: { page: number; providerId: string }) {
  return {
    queryFn: () =>
      ProviderClientsApi.readProviderClientsByProviderId({ path: { providerId }, query: { skip: (page - 1) * PER_PAGE, limit: PER_PAGE } }),
    queryKey: ["ProviderClients", { page, providerId }],
  }
}

function getProviderGroupsQueryOptions({ providerId }: { providerId: string }) {
  return {
    queryFn: () =>
      ProviderGroups.readProviderGroupsByProviderId({ path: { providerId } }),
    queryKey: ["providerGroups", { providerId }],
  }
}

export const Route = createFileRoute("/_layout/provider/providerclients/$providerId")({
  component: ProviderClients,
  validateSearch: (search) => ProviderClientsSearchSchema.parse(search),
})

function ProviderClientsTable() {
  const params = Route.useParams()
  const providerId = params.providerId
  const { page } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })

  const { data, isLoading, isPlaceholderData } = useQuery({
    ...getProviderClientsQueryOptions({ page, providerId }),
    placeholderData: (prevData) => prevData,
  })

  const { data: clientTypesData } = useQuery({
    queryKey: ["providerClientTypes", providerId],
    queryFn: () => ProviderClientTypes.listProviderClientTypes({ query: { providerId } }),
    enabled: !!providerId,
  })

  // Query to check for provider groups
  const { data: providerGroupsData, isLoading: providerGroupsLoading } = useQuery({
    ...getProviderGroupsQueryOptions({ providerId }),
    enabled: !!providerId,
  })

  const setPage = (page: number) =>
    navigate({
      search: (prev: { page: number }) => ({ ...prev, page }),
    })

  const ProviderClients = data?.data.slice(0, PER_PAGE) ?? []
  const count = data?.count ?? 0
  const providerGroups = providerGroupsData?.data ?? []
  const clientTypeMap = new Map((clientTypesData?.data ?? []).map((t) => [t.id, t.name]))

  if (isLoading) {
    return <PendingProviderClients />
  }

  if (ProviderClients.length === 0) {
    // Check if there are no provider groups
    if (!providerGroupsLoading && providerGroups.length === 0) {
      return (
        <EmptyState.Root>
          <EmptyState.Content>
            <EmptyState.Indicator>
              <FiSearch />
            </EmptyState.Indicator>
            <VStack textAlign="center">
              <EmptyState.Title>Provider group required</EmptyState.Title>
              <EmptyState.Description>
                You need to create a provider group before adding provider clients.{" "}
                <Link 
                  href={`/providergroups/${providerId}`}
                  color="blue.500" 
                  textDecoration="underline"
                >
                  Go to Provider Groups
                </Link>
              </EmptyState.Description>
            </VStack>
          </EmptyState.Content>
        </EmptyState.Root>
      )
    }

    // Normal empty state when provider groups exist but no clients
    return (
      <EmptyState.Root>
        <EmptyState.Content>
          <EmptyState.Indicator>
            <FiSearch />
          </EmptyState.Indicator>
          <VStack textAlign="center">
            <EmptyState.Title>You don't have any provider clients yet</EmptyState.Title>
            <EmptyState.Description>
              Add a new provider client to get started
            </EmptyState.Description>
          </VStack>
        </EmptyState.Content>
      </EmptyState.Root>
    )
  }

  return (
    <>
      <Table.Root size="sm" variant="outline">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader>Client ID</Table.ColumnHeader>
            <Table.ColumnHeader>Name</Table.ColumnHeader>
            <Table.ColumnHeader>Email</Table.ColumnHeader>
            <Table.ColumnHeader>Client Type</Table.ColumnHeader>
            <Table.ColumnHeader>Group</Table.ColumnHeader>
            <Table.ColumnHeader>Pension</Table.ColumnHeader>
            <Table.ColumnHeader>Web</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {ProviderClients?.map((pc: any) => {
            const displayName = [pc.prefername || pc.firstname, pc.lastname].filter(Boolean).join(" ") || "—"
            return (
              <Table.Row key={pc.id} opacity={isPlaceholderData ? 0.5 : 1} cursor="pointer" _hover={{ bg: "gray.50" }} onClick={() => navigate({ to: "/provider/providerclients/edit", search: { providerClientId: pc.id } })}>
                <Table.Cell>
                  <Badge colorPalette="gray" variant="outline" fontFamily="mono" size="sm">
                    {pc.providerClientId}
                  </Badge>
                </Table.Cell>
                <Table.Cell fontWeight="medium">{displayName}</Table.Cell>
                <Table.Cell color={!pc.email ? "gray.400" : "inherit"}>{pc.email || "—"}</Table.Cell>
                <Table.Cell>
                  {pc.providerClientTypeId ? (
                    <Badge colorPalette="purple" size="sm">{clientTypeMap.get(pc.providerClientTypeId) ?? "—"}</Badge>
                  ) : (
                    <span style={{ color: "#A0AEC0" }}>—</span>
                  )}
                </Table.Cell>
                <Table.Cell color={!pc.providerGroupName ? "gray.400" : "inherit"}>{pc.providerGroupName || "—"}</Table.Cell>
                <Table.Cell>
                  {pc.pensionType ? (
                    <Badge colorPalette="blue" size="sm">{pc.pensionType}</Badge>
                  ) : (
                    <span style={{ color: "#A0AEC0" }}>—</span>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <Badge colorPalette={pc.is_webEnabled ? "green" : "gray"} size="sm">
                    {pc.is_webEnabled ? "Yes" : "No"}
                  </Badge>
                </Table.Cell>
              </Table.Row>
            )
          })}
        </Table.Body>
      </Table.Root>
      <Flex justifyContent="flex-end" mt={4}>
        <PaginationRoot
          count={count}
          pageSize={PER_PAGE}
          onPageChange={({ page }) => setPage(page)}
        >
          <Flex>
            <PaginationPrevTrigger />
            <PaginationItems />
            <PaginationNextTrigger />
          </Flex>
        </PaginationRoot>
      </Flex>
    </>
  )
}

function ProviderClients() {
  const params = Route.useParams()
  const providerId = params.providerId
  
  // Query to check for provider groups to conditionally show AddProviderClient
  const { data: providerGroupsData, isLoading: providerGroupsLoading } = useQuery({
    ...getProviderGroupsQueryOptions({ providerId }),
    enabled: !!providerId,
  })

  const providerGroups = providerGroupsData?.data ?? []
  const hasProviderGroups = !providerGroupsLoading && providerGroups.length > 0

  return (
    <Container maxW="full">
      <Heading size="lg" pt={12}>
        Provider Clients Management
      </Heading>
      {hasProviderGroups && <AddProviderClient />}
      <ProviderClientsTable />
    </Container>
  )
}